import { DesignResource, useGetProjectsQuery, usePostDesignsByDesignReassignMutation } from '@/api/serverApi'
import { Button, LoadingSpinner, SearchableSelect, useErrorHandler } from '@cfs-fixings/shared'
import { SelectOption } from '@cfs-fixings/shared/src/components/dropdown-input/MinimalSelect'
import toastHelper from '@cfs-fixings/shared/src/helpers/toastHelper'
import { valibotResolver } from '@hookform/resolvers/valibot'
import { useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import * as v from 'valibot'

const reassignDesignSchema = v.object({
  project_id: v.nonOptional(v.string([v.minLength(1, 'A project is required')]), 'A project is required'),
})

type ReassignDesignFormInputs = v.Input<typeof reassignDesignSchema>

type PropTypes = {
  design: DesignResource,
  onCompleted?: (project: DesignResource) => void,
  onClose?: () => void,
}

function ReassignDesignForm ({ design, onCompleted, onClose }: PropTypes) {
  const textSpec = {
    projectField: {
      placeholder: 'Choose a project',
    },
    buttonLabels: {
      cancel: 'Cancel',
      create: 'Move Design',
    },
    successToastMessage: {
      title: 'Design moved',
      body: 'Your design has been moved to the selected project.',
    },
    errorToastMessage: {
      title: 'Something went wrong',
      body: 'Please try again later.',
    },
  }

  const errorHandler = useErrorHandler()

  const { handleSubmit, formState: { errors }, setError, reset, control } = useForm<ReassignDesignFormInputs>({
    resolver: valibotResolver(reassignDesignSchema),
  })

  const [customerSearchQuery, setCustomerSearchQuery] = useState('')

  const [reassign, { isLoading: isSubmitting }] = usePostDesignsByDesignReassignMutation()

  const { data: response } = useGetProjectsQuery({
    search: customerSearchQuery,
    perPage: 5,
  })

  const onSubmit: SubmitHandler<ReassignDesignFormInputs> = (data) => {
    reassign({
      design: design.id,
      body: data,
    })
      .unwrap()
      .then((response) => {
        onCompleted?.(response.data)
        toastHelper.success(textSpec.successToastMessage.title, textSpec.successToastMessage.body)
        reset()
      })
      .catch((error) => {
        if (error?.status === 422) {
          errorHandler(error, setError)
        } else {
          toastHelper.error(textSpec.errorToastMessage.title, error.data?.message ?? textSpec.errorToastMessage.body)
        }
      })
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col items-start w-full max-w-[615px]"
    >
      {
        response?.data && <div className="my-[17px] w-full flex flex-col gap-[30px]">
          {/* Project Picker */}
          <div>
            <Controller
              control={control}
              name={'project_id'}
              render={({ field }) => {
                const handleOnChange = (e: SelectOption) => field.onChange(e.value)
                return (
                  <SearchableSelect
                    placeholder={textSpec.projectField.placeholder}
                    defaultValue={field.value}
                    options={response?.data.map((project) => ({ label: project.name, value: project.id })) ?? []}
                    onChange={(option) => handleOnChange(option)}
                    onSearchChange={(value) => setCustomerSearchQuery(value)}
                    error={errors?.project_id?.message as string}
                  />
                )
              }}
            />
          </div>
        </div>
      }

      {
        !response?.data && <div className="w-full flex items-center justify-center my-8">
          <LoadingSpinner className="w-4 h-4" />
        </div>
      }

      <div className="w-full flex flex-row justify-end gap-2.5 mt-[17px]">
        <Button
          key="close"
          variant="secondary"
          isLoading={isSubmitting}
          onClick={onClose}
        >
          {textSpec.buttonLabels.cancel}
        </Button>
        <Button
          key="create"
          variant="dark"
          isLoading={isSubmitting}
          type="submit"
        >
          {textSpec.buttonLabels.create}
        </Button>
      </div>
    </form>
  )
}

export default ReassignDesignForm
